import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  contractDocumentsApi,
  ReplaceContractDocumentDto,
  UpdateContractDocumentDto,
  UploadContractDocumentDto,
} from '../../../api/contracts';
import { notifications } from '@mantine/notifications';

export const useContractDocuments = (contractId: string) => {
  return useQuery({
    queryKey: ['contractDocuments', contractId],
    queryFn: () => contractDocumentsApi.list(contractId),
    enabled: !!contractId,
  });
};

export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      file,
      data,
    }: {
      contractId: string;
      file: File;
      data: UploadContractDocumentDto;
    }) => contractDocumentsApi.upload(contractId, file, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contractDocuments', variables.contractId] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.contractId] });
      notifications.show({
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to upload document',
        color: 'red',
      });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contractDocumentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractDocuments'] });
      notifications.show({
        title: 'Success',
        message: 'Document deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete document',
        color: 'red',
      });
    },
  });
};

export const useUpdateDocumentMetadata = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      documentId,
      data,
    }: {
      contractId: string;
      documentId: string;
      data: UpdateContractDocumentDto;
    }) => contractDocumentsApi.update(contractId, documentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contractDocuments', variables.contractId] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.contractId] });
      queryClient.invalidateQueries({ queryKey: ['contractAuditLogs', variables.contractId] });
      notifications.show({
        title: 'Success',
        message: 'Document updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update document',
        color: 'red',
      });
    },
  });
};

export const useReplaceDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      documentId,
      file,
      data,
    }: {
      contractId: string;
      documentId: string;
      file: File;
      data: ReplaceContractDocumentDto;
    }) => contractDocumentsApi.replace(contractId, documentId, file, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contractDocuments', variables.contractId] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.contractId] });
      queryClient.invalidateQueries({ queryKey: ['contractAuditLogs', variables.contractId] });
      notifications.show({
        title: 'Success',
        message: 'Document replaced successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to replace document',
        color: 'red',
      });
    },
  });
};

export const useDownloadDocument = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const { url } = await contractDocumentsApi.getUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to download document',
        color: 'red',
      });
    },
  });
};

